import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";
import type {
  DocumentChangeAuditQueryParamsDTO,
  DocumentChangeAuditsResponseDTO,
  DocumentChangeReportSummaryResponseDTO,
} from "../models/DocumentChangeAuditDTOs";

class APIError extends Error {
  public status: number;
  public data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
};

function buildQueryString(
  params?: DocumentChangeAuditQueryParamsDTO,
): string {
  if (!params) return "";

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

async function fetchWithAuth<T>(url: string): Promise<T> {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      (errorData as { message?: string }).message ||
        `HTTP error! status: ${response.status}`,
      response.status,
      errorData,
    );
  }

  return (await response.json()) as T;
}

export const getDocumentChangeAudits = async (
  params?: DocumentChangeAuditQueryParamsDTO,
): Promise<DocumentChangeAuditsResponseDTO> => {
  const url = `${API_BASE_URL}/api/document-change-audits${buildQueryString(params)}`;
  return fetchWithAuth<DocumentChangeAuditsResponseDTO>(url);
};

export const getDocumentChangeReportSummary = async (
  params?: DocumentChangeAuditQueryParamsDTO,
): Promise<DocumentChangeReportSummaryResponseDTO> => {
  const url = `${API_BASE_URL}/api/document-change-audits/summary${buildQueryString(params)}`;
  return fetchWithAuth<DocumentChangeReportSummaryResponseDTO>(url);
};
